import { useEffect, useState } from "react";
import { Marker, useMapEvents } from "react-leaflet";

interface LocationMarkerProps {
  position: { lat: number; lng: number } | null;
  setPosition: (pos: { lat: number; lng: number }) => void;
  onMapClick?: () => void;
  onLocationResolved?: (location: string) => void;
}

// Reverse geocode: coordinates → address using Nominatim
async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    
    if (data.address) {
      // Build location string: "City, State, Country" or "State, Country" or "Country"
      const parts = [];
      if (data.address.city || data.address.town || data.address.village) {
        parts.push(data.address.city || data.address.town || data.address.village);
      }
      if (data.address.state) {
        parts.push(data.address.state);
      }
      if (data.address.country) {
        parts.push(data.address.country);
      }
      
      return parts.length > 0 ? parts.join(", ") : null;
    }
    return null;
  } catch (error) {
    console.error("Reverse geocoding failed:", error);
    return null;
  }
}

// Forward geocode: address → coordinates using Nominatim
export async function forwardGeocode(address: string): Promise<Array<{ lat: number; lng: number; name: string }>> {
  if (!address || address.trim().length < 2) {
    return [];
  }
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=5`
    );
    const data = await response.json();
    
    return data.map((result: any) => ({
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      name: result.display_name
    }));
  } catch (error) {
    console.error("Forward geocoding failed:", error);
    return [];
  }
}

export function LocationMarker({ position, setPosition, onMapClick, onLocationResolved }: LocationMarkerProps) {
  const [isGeocoding, setIsGeocoding] = useState(false);
  
  const map = useMapEvents({
    async click(e) {
      const coords = { lat: e.latlng.lat, lng: e.latlng.lng };
      setPosition(coords);
      onMapClick?.();
      
      // Reverse geocode the clicked location
      if (onLocationResolved && !isGeocoding) {
        setIsGeocoding(true);
        const locationName = await reverseGeocode(coords.lat, coords.lng);
        if (locationName) {
          onLocationResolved(locationName);
        }
        setIsGeocoding(false);
      }
    },
  });
  
  // Invalidate size after short delay to ensure map renders correctly in dialog
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);
    return () => clearTimeout(timer);
  }, [map]);
  
  return position ? <Marker position={[position.lat, position.lng]} /> : null;
}
